import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import './ConsistencyGuard.css';
import LeftPanel from './components/LeftPanel';
import RightPanel from './components/RightPanel';
import { getConsistencyGuardSummaryApi, exportApprovedInconsistenciesXlsApi, getAllProjects, Project, getAssignableReviewersApi, AssignableReviewer } from '../../services/AxiosServices';
import { formatSeverity } from '../../utils/consguard/severity';

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const formatCategory = (cat: string) => {
  if (!cat) return 'Component List';
  return cat.replace(/_/g, ' ');
};

const formatStatus = (status: string) => {
  if (!status) return 'Open';
  const s = status.toUpperCase();
  if (s === 'OPEN') return 'Open';
  if (s === 'IN_REVIEW' || s === 'REVIEW') return 'Review';
  if (s === 'APPROVED' || s === 'RESOLVED') return 'Approved';
  if (s === 'ARCHIVED' || s === 'ARCHIVE') return 'Archived';
  return status;
};

const ConsistencyGuard: React.FC = () => {
  const location = useLocation();
  const queryId = new URLSearchParams(location.search).get('id');
  const queryInconsistencyNumber = new URLSearchParams(location.search).get('inconsistency_number');

  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<any>({});
  const [selectedId, setSelectedId] = useState<string | null>(queryId);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [assignableReviewersList, setAssignableReviewersList] = useState<AssignableReviewer[]>([]);

  useEffect(() => {
    const fetchAssignableReviewers = async () => {
      try {
        let token = '';
        try {
          token = await getAccessTokenSilently();
        } catch (tokenErr) {
          console.error('Unable to get access token silently:', tokenErr);
        }
        if (!token) return;
        const res: any = await getAssignableReviewersApi(token);
        if (res && Array.isArray(res)) {
          setAssignableReviewersList(res);
        }
      } catch (err) {
        console.error('Error fetching assignable reviewers:', err);
      }
    };
    fetchAssignableReviewers();
  }, [getAccessTokenSilently]);

  // Fetch projects list on initial load
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        let token = '';
        try {
          token = await getAccessTokenSilently();
        } catch (tokenErr) {
          console.warn('Unable to get access token silently:', tokenErr);
        }
        const projectsData = await getAllProjects<Project[]>(token);
        if (projectsData && Array.isArray(projectsData) && projectsData.length > 0) {
          setProjects(projectsData);
        }
      } catch (err: any) {
        console.error('Error fetching project list:', err);
      }
    };
    fetchProjects();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const storedParam = sessionStorage.getItem('cg-selected-project') || '';
    if (!storedParam && projects.length > 0) {
      setSelectedProjectId(projects[0].project_id);
    } else if (storedParam && selectedProjectId !== storedParam) {
      setSelectedProjectId(storedParam);
    }
  }, [location.search, location.pathname, projects]);

  useEffect(() => {
    if (!selectedId || !data.inconsistencies) return;
    const item = data.inconsistencies.find((i: any) => i.inconsistencyNumber === selectedId);
    if (!item) return;

    const params = new URLSearchParams(window.location.search);
    if (item.inconsistencyNumber && item.inconsistencyNumber === selectedId) {
      params.set('inconsistency_number', item.inconsistencyNumber);
    }
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [selectedId, data.inconsistencies]);

  useEffect(() => {
    if (!selectedId || !data.inconsistencies) return;
    const item = data.inconsistencies.find((i: any) => i.inconsistencyNumber === selectedId);
    if (!item) return;

    const params = new URLSearchParams(window.location.search);
    if (item.inconsistencyNumber && item.inconsistencyNumber === selectedId) {
      params.set('inconsistency_number', item.inconsistencyNumber);
    }
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [selectedId, data.inconsistencies]);

  const handleProjectChange = useCallback((newProjectId: string) => {
    sessionStorage.setItem('cg-selected-project', newProjectId);
    setSelectedProjectId(newProjectId);
    const params = new URLSearchParams();
    params.set('project', newProjectId);
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, [getAccessTokenSilently]);

  // Resizing state
  const [leftPanelWidth, setLeftPanelWidth] = useState<number | string>('50%');
  const isResizing = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchSummaryData = useCallback(async (projectId: string) => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      let token = '';
      try {
        token = await getAccessTokenSilently();
      } catch (tokenErr) {
        console.warn('Unable to get silently access token:', tokenErr);
      }

      const res: any = await getConsistencyGuardSummaryApi(projectId, token);

      if (res) {
        const summaryInfo = Array.isArray(res) ? {} : (res.summary || res.dossier || res);
        const instances = Array.isArray(res)
          ? res
          : (res.conflicted_rules_instances || res.inconsistencies || res.items || []);

        const mappedInconsistencies = instances.map((item: any, index: number) => {
          const reviewerName = typeof item.reviewer === 'object'
            ? (item.reviewer?.user_name || item.reviewer?.name || 'SYSTEM')
            : (item.reviewer || 'SYSTEM');

          const evidenceList = (item.evidence || item.evidences || item.evidence_list || item.passages || []).map((ev: any, evIdx: number) => ({
            id: ev.id || `ev-${index}-${evIdx}`,
            title: ev.title || ev.name || ev.quote || item.title || 'Evidence',
            documentName: ev.document_name || ev.documentName || ev.doc_name || ev.source || 'Document',
            snippet: ev.snippet || ev.text || ev.passage || ev.content || '',
            label: ev.label || ev.status || 'Choose appropriate'
          }));

          const scanId = item.scan_id || item.scanId || item.scan_ID || summaryInfo.scan_id || summaryInfo.project_id || projectId;
          const ruleId = item.rule_id || item.ruleId || item.rule_ID || item.id;
          const inconsistencyId = item.inconsistency_id || item.inconsistencyId || item.id;
          const inconsistencyNumber = item.inconsistency_number || `INC_${index + 1}`;
          return {
            id: inconsistencyNumber || item.id,
            scan_id: scanId,
            rule_id: ruleId,
            scanId: scanId,
            ruleId: ruleId,
            inconsistency_id: inconsistencyId,
            inconsistencyNumber: inconsistencyNumber,
            title: item.title || item.rule_title || item.name || 'Untitled Inconsistency',
            category: formatCategory(item.rule_category || item.category),
            categoryDetail: item.category_detail || item.categoryDetail || formatCategory(item.rule_category || item.category),
            severity: formatSeverity(item.severity) || item.severity || 'MEDIUM',
            reviewer: reviewerName,
            status: formatStatus(item.activity_status || item.status),
            reason: item.reasoning || item.reason || item.description || item.explanation || '',
            firstDetected: formatDate(item.first_detected || item.detected_date || item.detected || item.created_at || item.created_date) || 'Today',
            lastChange: formatDate(item.last_change || item.updated_at || item.modified_date) || '',
            notes: item.notes || '',
            evidence: evidenceList
          };
        });

        const distinctReviewers = Array.from(
          new Set(mappedInconsistencies.map((i: any) => i.reviewer).filter(Boolean))
        ) as string[];

        const totalCount = summaryInfo.total_active ?? mappedInconsistencies.length;
        const resolvedCount = mappedInconsistencies.filter((i: any) => i.status === 'Approved' || i.status === 'Resolved').length;
        const resolvedPercentage = summaryInfo.percentage_approved ? Math.round(summaryInfo.percentage_approved) : (totalCount > 0 ? Math.round((resolvedCount / totalCount) * 100) : 0);

        const dossierInfo = {
          name: summaryInfo.project_name || summaryInfo.project_id || projectId,
          owner: summaryInfo.owner || 'SYSTEM',
          resolvedPercentage: resolvedPercentage,
          totalInconsistencies: totalCount
        };

        setData({
          dossier: dossierInfo,
          reviewers: distinctReviewers.length > 0 ? distinctReviewers : ['SYSTEM'],
          inconsistencies: mappedInconsistencies
        });

        if (mappedInconsistencies.length > 0) {
          setSelectedId(prev => {
            if (prev && mappedInconsistencies.some((i: any) => i.id === prev)) {
              return prev;
            }
            if (queryInconsistencyNumber) {
              const byNumber = mappedInconsistencies.find((i: any) => (i.inconsistencyNumber === queryInconsistencyNumber));
              if (byNumber) return byNumber.id;
            }
            if (queryId) {
              const byId = mappedInconsistencies.find((i: any) => i.id === queryId);
              if (byId) return byId.id;
            }
            const sortedInconsistencies = [...mappedInconsistencies].sort((a, b) => {
              const numA = parseInt(a.inconsistencyNumber?.split('_')[1] || 0, 10);
              const numB = parseInt(b.inconsistencyNumber?.split('_')[1] || 0, 10);
              return numA - numB;
            });

            return sortedInconsistencies[0]?.inconsistencyNumber || sortedInconsistencies[0]?.id;
          });
        }
      }
    } catch (err: any) {
      console.error('Error fetching consguard summary data:', err);
      setError(err.message || 'Failed to fetch inconsistency summary data');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, queryId, queryInconsistencyNumber, selectedProjectId]);

  useEffect(() => {
    fetchSummaryData(selectedProjectId);
  }, [selectedProjectId, fetchSummaryData]);

  const handleSelectRow = (id: string) => {
    setSelectedId(id);
  };

   const handleSeverityChange = useCallback((incId: string, newValue: string) => {
     setData(prevData => ({
       ...prevData,
       inconsistencies: (prevData.inconsistencies || []).map(
         (item: any) => item.id === incId ? { ...item, severity: newValue } : item,
       ),
     }));
   }, []);

   const handleRefreshData = useCallback(() => {
     if (selectedProjectId) {
       fetchSummaryData(selectedProjectId);
     }
   }, [selectedProjectId, fetchSummaryData]);

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    if (!isCollapsed) {
      setLeftPanelWidth('300px');
    } else {
      setLeftPanelWidth('50%');
    }
  };

  const getNum = (str: string) => parseInt(str?.split('_')[1] || '0', 10);

  // Navigation handlers 
  // TODO: clean and optimise the following code
  const handleNext = () => {
    if (!data.inconsistencies || !selectedId) return;
    const sortedInconsistencies = [...data.inconsistencies].sort((a, b) =>
      getNum(a.id) - getNum(b.id)
    );
    const index = sortedInconsistencies.findIndex((item: any) =>
      getNum(item.id) === getNum(selectedId)
    );    
    if (index >= 0 && index < data.inconsistencies.length - 1) {
      setSelectedId(sortedInconsistencies[index + 1].id);
    }
  };
  
  const handlePrev = () => {
    if (!data.inconsistencies || !selectedId) return;
    const sortedInconsistencies = [...data.inconsistencies].sort((a, b) =>
      getNum(a.id) - getNum(b.id)
    );
    const index = sortedInconsistencies.findIndex((item: any) =>
      getNum(item.id) === getNum(selectedId)
    );
    if (index > 0) {
      setSelectedId(sortedInconsistencies[index - 1].id);
    }
  };

  const handleExportXls = async () => {
    setIsExporting(true);
    try {
      let token = '';
      try {
        token = await getAccessTokenSilently();
      } catch (tokenErr) {
        console.warn('Unable to get access token silently:', tokenErr);
      }
      const response = await exportApprovedInconsistenciesXlsApi(selectedProjectId, token);
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Consistency_Guard_Approved_${selectedProjectId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (err: any) {
      console.error('Export failed:', err);
      alert('Export failed. No approved inconsistencies found or an error occurred.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    if (!selectedId) return;
    const newData = { ...data };
    const index = newData.inconsistencies.findIndex((item: any) => item.id === selectedId);
    if (index >= 0) {
      newData.inconsistencies[index].status = 'Review';
      setData(newData);
    }
  };

  const handleArchive = () => {
    if (!selectedId) return;
    const newData = { ...data };
    const index = newData.inconsistencies.findIndex((item: any) => item.id === selectedId);
    if (index >= 0) {
      newData.inconsistencies[index].status = 'Archived';
      setData(newData);
    }
  };

  // Resizer handlers
  const startResizing = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (isCollapsed) return;
    isResizing.current = true;
  }, [isCollapsed]);

  const stopResizing = React.useCallback(() => {
    isResizing.current = false;
  }, []);

  const resize = React.useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (isResizing.current && containerRef.current && !isCollapsed) {
        const containerRect = containerRef.current.getBoundingClientRect();
        let newWidth = mouseMoveEvent.clientX - containerRect.left;
        const minWidth = 400;
        const maxWidth = containerRect.width - 400;
        if (newWidth < minWidth) newWidth = minWidth;
        if (newWidth > maxWidth) newWidth = maxWidth;
        setLeftPanelWidth(`${newWidth}px`);
      }
    },
    [isCollapsed]
  );

  useEffect(() => {
    window.addEventListener("mousemove", resize);
    window.addEventListener("mouseup", stopResizing);
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [resize, stopResizing]);

  const selectedItem = data.inconsistencies?.find((item: any) => item.id === selectedId);

  return (
    <div className="cg-container" ref={containerRef}>
      <div
        className={`cg-left-panel ${isCollapsed ? 'collapsed' : ''}`}
        style={{ width: isCollapsed ? '300px' : leftPanelWidth }}
      >
        {loading && <div style={{ padding: '12px 16px', color: '#64748b', fontSize: '13px' }}>Loading inconsistency summary...</div>}
        {error && <div style={{ padding: '12px 16px', color: '#ef4444', fontSize: '13px' }}>Notice: {error}</div>}
        <LeftPanel
          data={data}
          selectedId={selectedId}
          onSelectRow={handleSelectRow}
          isCollapsed={isCollapsed}
          onExport={handleExportXls}
          isExporting={isExporting}
          selectedProjectId={selectedProjectId}
          projects={projects}
          onSeverityChange={handleSeverityChange}
          onRefreshData={handleRefreshData}
          onProjectChange={handleProjectChange}
          assignableReviewersData={assignableReviewersList}
        />
        {!isCollapsed && (
          <div className="cg-resizer" onMouseDown={startResizing} />
        )}
      </div>

      <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
        <button className="cg-collapse-toggle" onClick={handleToggleCollapse}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transform: isCollapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.3s' }}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <RightPanel
          selectedItem={selectedItem}
          onNext={handleNext}
          onPrev={handlePrev}
          onRefreshData={handleRefreshData}
          onReset={handleReset}
          onArchive={handleArchive}
        />
      </div>
    </div>
  );
};

export default ConsistencyGuard;
